import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { WizardRoutesEnum } from 'utils/actions';

export type WizardNavigationContextType = {
  /**
   * Current section (from the top navigation)
   */
  currentSection: number;

  /**
   * Complete sections (from the top navigation)
   */
  completeSections: Array<number>;

  /**
   * Currently active screen route
   */
  currentScreen: WizardRoutesEnum;

  /**
   * Navigates one screen back
   */
  back: () => void;

  /**
   * Advance one screen further
   */
  next: () => void;

  /**
   * Jump to any screen
   */
  goTo: (screen: WizardRoutesEnum) => void;

  /**
   * Returns list of already completed sections (topbar)
   */
  resolveCompleteScreens: () => Array<WizardRoutesEnum>;

  /**
   * Order of the screens
   */
  ScreenOrder: Array<WizardRoutesEnum>;
};

export const WizardNavigationContext = createContext<WizardNavigationContextType>(null!);

export const WizardNavigationContextProvider = ({ children }: PropsWithChildren) => {
  const ScreenOrder = [
    WizardRoutesEnum.PrivateData,
    WizardRoutesEnum.InsuranceHolder,
    WizardRoutesEnum.Summary
  ];

  const [currentScreen, setCurrentScreen] = useState(WizardRoutesEnum.PrivateData);

  const currentSection = useMemo(() => {
    switch (currentScreen) {
      case WizardRoutesEnum.Summary:
        return 2;
      case WizardRoutesEnum.InsuranceHolder:
        return 1;
      default:
        return 0;
    }
  }, [currentScreen]);

  const completeSections = useMemo(() => {
    switch (currentScreen) {
      case WizardRoutesEnum.Summary:
        return [0, 1];
      case WizardRoutesEnum.InsuranceHolder:
        return [0];
      default:
        return [];
    }
  }, [currentScreen]);

  function goTo(screen: WizardRoutesEnum): void {
    setCurrentScreen(screen);
  }

  function resolveCompleteScreens(): Array<WizardRoutesEnum> {
    const reversed = [...ScreenOrder].reverse();
    const index = reversed.indexOf(currentScreen);
    return reversed.slice(index);
  }

  function back(): void {
    const index = ScreenOrder.indexOf(currentScreen) - 1;
    goTo(ScreenOrder[index]);
  }

  function next(): void {
    const index = ScreenOrder.indexOf(currentScreen) + 1;
    goTo(ScreenOrder[index]);
  }

  const ctx = useMemo(() => {
    return {
      currentScreen,
      currentSection,
      completeSections,
      ScreenOrder,
      next,
      back,
      goTo,
      resolveCompleteScreens
    };
  }, [
    currentScreen,
    currentSection,
    completeSections,
    ScreenOrder,
    next,
    back,
    goTo,
    resolveCompleteScreens
  ]);

  return (
    <WizardNavigationContext.Provider value={ctx}>{children}</WizardNavigationContext.Provider>
  );
};

export const useWizardNavigation = (): WizardNavigationContextType => {
  const wizardNavigationContext = useContext(WizardNavigationContext);

  if (!wizardNavigationContext) {
    throw new Error(
      'To use "useWizardNavigation" some of the parent components must be in <WizardNavigationContextProvider>'
    );
  }

  return wizardNavigationContext;
};
